import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

const DESKTOP_MQ = "(min-width: 1024px)";
let historiqueMatchMedia;

// jsx-tabs inserts a <nav> and hides non-active panels on init (~100ms after
// DOMContentLoaded), which shifts the page layout just after our ScrollTrigger
// positions are cached. Listen to its "done" event and recalculate positions.
// This is added once at module load so it stays active for the page lifetime.
document.addEventListener("jsx-tabs:initialized", () => {
  ScrollTrigger.refresh();
});

// Track only the ScrollTriggers created by this module so clearScrollTriggers()
// doesn't accidentally kill triggers owned by other bundles (e.g. jsx-accordion,
// jsx-tabs) even though those use separate GSAP instances today.
let ownedScrollTriggers = [];

// Animation utility functions
export const animationUtils = {
  // Add hidden class to prevent flashing
  addHiddenClass() {
    const elementsToAnimate = document.querySelectorAll(".info-card");
    elementsToAnimate.forEach((element) => {
      element.classList.add("gsap-hidden");
    });
  },

  // Remove all hidden classes as fallback
  removeAllHiddenClasses() {
    document.querySelectorAll(".gsap-hidden").forEach((element) => {
      element.classList.remove("gsap-hidden");
    });
  },

  // Clear only ScrollTrigger instances owned by this module
  clearScrollTriggers() {
    historiqueMatchMedia?.revert();
    historiqueMatchMedia = null;
    ownedScrollTriggers.forEach((st) => st.kill());
    ownedScrollTriggers = [];
  },
};

/*--------------------------------------------------
 * Historique content scale — Capcap-style (desktop only)
 * Scales .historique__content from 0.85 → 1 on scroll
 *-------------------------------------------------*/

const initHistoriqueScaleAnimation = () => {
  historiqueMatchMedia?.revert();
  historiqueMatchMedia = gsap.matchMedia();

  historiqueMatchMedia.add(DESKTOP_MQ, () => {
    const content = document.querySelector(".historique__content");

    if (!content) return;

    gsap.set(content, {
      scale: 0.75,
      transformOrigin: "center center",
    });

    // Matches Capcap nectar offsets: start 0 → end 50
    gsap.to(content, {
      scale: 1,
      ease: "none",
      scrollTrigger: {
        trigger: content,
        start: "top bottom",
        end: "+=500",
        scrub: true,
      },
    });

    return () => {
      gsap.set(content, { clearProps: "transform" });
    };
  });
};

/*--------------------------------------------------
 * Scroll-triggered animations
 *-------------------------------------------------*/

export const initScrollAnimations = () => {
  // Clear ScrollTriggers owned by this module
  animationUtils.clearScrollTriggers();

  initHistoriqueScaleAnimation();

  // .info-card entrance animation
  // Uses individual ScrollTrigger instances instead of ScrollTrigger.batch because
  // batch does not fire onEnter for elements already in the viewport on creation,
  // and its scroll listener misbehaves after an immediate refresh(). Individual
  // triggers reliably fire onEnter both on creation (for in-viewport elements)
  // and on scroll (for below-fold elements).
  const batchElements = document.querySelectorAll(".info-card, .flip-card");

  if (batchElements.length > 0) {
    gsap.set(batchElements, { opacity: 0, y: 20, immediateRender: true });

    batchElements.forEach((card, index) => {
      card.classList.remove("gsap-hidden");

      // Stagger delay simulates the original grid: [1, 4] batch stagger —
      // cards in the same row (groups of 4) cascade left-to-right.
      const staggerDelay = (index % 4) * 0.15;

      const st = ScrollTrigger.create({
        trigger: card,
        start: "top 95%",
        once: true,
        onEnter: () => {
          gsap.to(card, {
            opacity: 1,
            y: 0,
            duration: 0.5,
            delay: staggerDelay,
            ease: "power2.inOut",
          });
        },
      });

      ownedScrollTriggers.push(st);
    });
  }

  // General-purpose delayed refresh: catches layout shifts from any block that
  // mutates the DOM at ~100ms but doesn't dispatch a named event (accordions,
  // lazy images, custom fonts, etc.).
  setTimeout(() => ScrollTrigger.refresh(), 400);

  // Individual element animations (.example)
  const individualElements = document.querySelectorAll(
    "h2:not(.marquee__heading)",
  );

  individualElements.forEach((element) => {
    gsap.set(element, { opacity: 0, y: 20, immediateRender: true });
    element.classList.remove("gsap-hidden");

    const st = ScrollTrigger.create({
      trigger: element,
      start: "top 95%",
      once: true,
      onEnter: () => {
        gsap.to(element, {
          opacity: 1,
          y: 0,
          duration: 0.4,
          ease: "power2.inOut",
        });
      },
    });

    ownedScrollTriggers.push(st);
  });
};

// Main animation initialization
export const initAllAnimations = () => {
  // Add hidden classes first
  animationUtils.addHiddenClass();

  // Small delay for scroll animations to ensure DOM is ready
  setTimeout(() => {
    initScrollAnimations();

    // Failsafe to remove hidden classes
    setTimeout(animationUtils.removeAllHiddenClasses, 1000);
  }, 100);
};
